
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  CheckCircle2,
  Phone,
  ShieldCheck,
  X,
} from "lucide-react";
import { useState } from "react";

/**
 * Post-login banner: reminds the user to validate the remaining mandatory
 * items (phone OTP + government ID upload). Dismissible per session.
 *
 * Mandatory items:
 *  - Phone verification (via OTP)
 *  - Government ID upload (manual admin review)
 *
 * Email is always verified by the registration flow, so it's not listed.
 */
export function SetupBanner() {
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  // Lazy-init: read dismissal state from sessionStorage on first render so we
  // don't need a setState-in-effect. The banner is only shown inside AppShell
  // (i.e. user is always defined), but we still guard defensively.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!user) return false;
    if (typeof window === "undefined") return false;
    const key = `horndeliver.setup-banner.dismissed.${user.id}`;
    return window.sessionStorage.getItem(key) === "1";
  });

  if (!user || dismissed) return null;

  const phoneOk = !!user.phoneVerified;
  const govIdOk = user.trustStatus === "verified" || user.trustStatus === "pending";
  const allOk = phoneOk && govIdOk;
  // Admin users don't need to complete these steps.
  if (user.isAdmin) return null;
  // Once everything is verified, the banner is no longer needed.
  if (allOk) return null;

  const dismiss = () => {
    setDismissed(true);
    if (typeof window !== "undefined" && user) {
      window.sessionStorage.setItem(`horndeliver.setup-banner.dismissed.${user.id}`, "1");
    }
  };

  return (
    <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="flex-1 space-y-2">
            <div>
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                {t("auth.postLoginBannerTitle")}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {t("auth.postLoginBannerDesc")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <SetupItem
                icon={Phone}
                label={t("auth.verifyPhone")}
                done={phoneOk}
                onClick={() => navigate("app/profile")}
              />
              <SetupItem
                icon={ShieldCheck}
                label={t("auth.uploadGovId")}
                done={govIdOk}
                onClick={() => navigate("app/trust")}
              />
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label={t("nav.dismiss")}
            className="rounded-md p-1 text-amber-700/70 hover:bg-amber-100 dark:text-amber-300/70 dark:hover:bg-amber-900/30"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function SetupItem({
  icon: Icon,
  label,
  done,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  done: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="sm"
      variant={done ? "outline" : "default"}
      onClick={onClick}
      className={
        done
          ? "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
          : ""
      }
    >
      {done ? (
        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
      ) : (
        <Icon className="mr-1.5 h-3.5 w-3.5" />
      )}
      {label}
    </Button>
  );
}
